import React, { useEffect, useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { GetLanguageString } from "../../helper/Components";
import {
  LoadingSpinner,
  convertToDateString,
} from "../../../constants/const";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";

import { getMinMaxDates } from "../fiscalYearHelper";
import ShareTransferContextPanel, {
  getShareMaxTransferable,
} from "../ShareTransferContextPanel";

const MarkazShare = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const [loading, setLoading] = useState({
    submit: false,
    calculate: false,
  });

  const getDefaultShareMonth = () => {
    const today = new Date();
    return today.getDate() <= 20
      ? new Date(today.getFullYear(), today.getMonth() - 1, 1)
      : new Date(today.getFullYear(), today.getMonth(), 1);
  };

  const getShareMonthBounds = (shareMonthDate) => {
    if (!shareMonthDate) return { minDate: null, maxDate: new Date() };
    const year = shareMonthDate.getFullYear();
    const month = shareMonthDate.getMonth();
    const minDate = new Date(year, month, 1);
    const today = new Date();
    return { minDate, maxDate: today };
  };

  const defaultShareMonth = getDefaultShareMonth();
  const defaultProcessedDate = new Date();

  const [formData, setFormData] = useState({
    share_month: defaultShareMonth,
    processed_date: defaultProcessedDate,
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});
  const [justificationFile, setJustificationFile] = useState(null);

  const { minDate: fiscalMinDate } = getMinMaxDates();
  const getLatestShareMonthMaxDate = () => {
    const today = new Date();
    return today.getDate() <= 20
      ? new Date(today.getFullYear(), today.getMonth(), 0)
      : today;
  };
  const latestShareMonthMaxDate = getLatestShareMonthMaxDate();
  const { minDate, maxDate } = getShareMonthBounds(formData.share_month);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "amount") {
      const nextAmount = parseFloat(value);
      if (!(calculatedAmount > 0 && nextAmount > 0 && nextAmount < calculatedAmount)) {
        setJustificationFile(null);
        setErrors((prev) => ({ ...prev, justificationFile: "" }));
      }
    }
  };

  const handleShareMonthChange = (date) => {
    const selectedMonth = date ?? getDefaultShareMonth();
    const bounds = getShareMonthBounds(selectedMonth);
    let updatedProcessedDate = formData.processed_date;
    if (updatedProcessedDate < bounds.minDate || updatedProcessedDate > bounds.maxDate) {
      const today = new Date();
      if (today >= bounds.minDate && today <= bounds.maxDate) {
        updatedProcessedDate = today;
      } else {
        updatedProcessedDate = bounds.maxDate;
      }
    }
    setFormData((prev) => ({
      ...prev,
      share_month: selectedMonth,
      processed_date: updatedProcessedDate,
    }));
    setErrors((prev) => ({ ...prev, share_month: "", processed_date: "" }));
  };

  const [shareContext, setShareContext] = useState(null);
  const calculatedAmount = Number(shareContext?.minimumShare) || 0;
  const parsedFormAmount = parseFloat(formData.amount);
  const isDeficit =
    calculatedAmount > 0 &&
    !isNaN(parsedFormAmount) &&
    parsedFormAmount > 0 &&
    parsedFormAmount < calculatedAmount;

  useEffect(() => {
    if (!formData.share_month) return;

    setLoading((prev) => ({ ...prev, calculate: true }));
    const formattedDate = convertToDateString(formData.share_month);

    ApiFinanceLocal()
      .GetMarkazShareContext(loggedInUnitId, formattedDate)
      .then((res) => {
        const context = res.data || {};
        setShareContext(context);
        setFormData((prev) => ({
          ...prev,
          amount: (context.minimumShare ?? 0).toString(),
        }));
        setLoading((prev) => ({ ...prev, calculate: false }));
      })
      .catch((err) => {
        console.error(err);
        toast.error(t("funds_markaz_share_calculation_failed"));
        setLoading((prev) => ({ ...prev, calculate: false }));
      });
  }, [formData.share_month, loggedInUnitId, t]);

  const validate = () => {
    const temp = {};
    let isValid = true;

    if (!formData.share_month) {
      temp.share_month = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.processed_date) {
      temp.processed_date = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.amount || formData.amount.toString().trim() === "") {
      temp.amount = t("common_field_required_error");
      isValid = false;
    } else {
      const parsedAmount = parseFloat(formData.amount);
      const maxTransferable = getShareMaxTransferable(shareContext, "amanat");
      const totalInfaq = Number(shareContext?.totalInfaq) || 0;
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        temp.amount = t("amount_must_be_greater_than_zero");
        isValid = false;
      } else if (shareContext?.hasPendingRequest) {
        temp.amount = t("share_request_already_pending");
        isValid = false;
      } else if (parsedAmount > totalInfaq) {
        temp.amount = t("share_amount_exceeds_total_infaq");
        isValid = false;
      } else if (parsedAmount > maxTransferable) {
        temp.amount = t("share_amount_exceeds_account_balance");
        isValid = false;
      }
    }

    if (isDeficit) {
      if (!justificationFile) {
        temp.justificationFile = t("markaz_share_justification_required");
        isValid = false;
      } else if (justificationFile.size > 2 * 1024 * 1024) {
        temp.justificationFile = t("markaz_share_justification_size_limit");
        isValid = false;
      }
    }

    if (!formData.memo || formData.memo.toString().trim() === "") {
      temp.memo = t("common_field_required_error");
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const formatLocalDateString = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validate()) {
      return;
    }

    const enteredAmount = parseFloat(formData.amount);

    let finalMemo = formData.memo ? formData.memo.trim() : "";
    if (formData.share_month) {
      const monthName = formData.share_month.toLocaleString("en-US", { month: "long" });
      const year = formData.share_month.getFullYear();
      if (!finalMemo.toLowerCase().includes(monthName.toLowerCase())) {
        finalMemo = finalMemo ? `${finalMemo} (${monthName} ${year})` : `${monthName} ${year}`;
      }
    }

    const submitRequest = () => {
      setLoading((prev) => ({ ...prev, submit: true }));

      const request = isDeficit
        ? (() => {
            const payload = new FormData();
            payload.append("unitId", loggedInUnitId);
            payload.append("date", formatLocalDateString(formData.processed_date));
            payload.append("share_month", formatLocalDateString(formData.share_month));
            payload.append("amount", enteredAmount);
            payload.append("memo", finalMemo);
            payload.append("isRequest", "true");
            payload.append("File", justificationFile);
            return ApiFinanceLocal().MarkazShareTransferWithJustification(payload);
          })()
        : ApiFinanceLocal().MarkazShareTransfer({
            date: formatLocalDateString(formData.processed_date),
            share_month: formatLocalDateString(formData.share_month),
            amount: enteredAmount,
            memo: finalMemo,
            unitId: loggedInUnitId,
            isRequest: true,
          });

      request
        .then((res) => {
          const data = res.data;
          if (data?.statusCode === 200) {
            let msg = t("finance_markaz_share_amanat_requested");
            if (t(data.message) !== data.message) {
              msg = t(data.message);
            }
            swal({
              title: "Success",
              text: msg,
              icon: "success",
              buttons: "OK",
            }).then(() => {
              history.goBack();
            });
            return;
          }

          swal({
            title: "Error",
            text: data?.message || "Failed to add Markaz Share",
            icon: "error",
            buttons: "OK",
          });
          setLoading((prev) => ({ ...prev, submit: false }));
        })
        .catch((err) => {
          console.error(err);
          const raw = err?.response?.data;
          const message = typeof raw === "string" ? raw : raw?.message;
          swal({
            title: "Error",
            text: message && t(message) !== message ? t(message) : "Unable to connect to server. Please try again.",
            icon: "error",
            buttons: "OK",
          });
          setLoading((prev) => ({ ...prev, submit: false }));
        });
    };

    if (isDeficit) {
      swal({
        title: t("funds_markaz_share_request_confirm_title"),
        text: t("funds_markaz_share_request_confirm_text"),
        icon: "warning",
        buttons: {
          cancel: t("funds_markaz_share_request_confirm_btn_cancel"),
          confirm: t("funds_markaz_share_request_confirm_btn_proceed"),
        },
      }).then((confirmed) => {
        if (confirmed) {
          submitRequest();
        }
      });
      return;
    }

    submitRequest();
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>{<GetLanguageString props="funds_markaz_share" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <ShareTransferContextPanel
                context={shareContext}
                loading={loading.calculate}
                minimumLabelKey="share_minimum_markaz"
              />
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_share_month" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select month"
                      className="form-control no-drag"
                      selected={formData.share_month}
                      onChange={handleShareMonthChange}
                      showMonthYearPicker
                      minDate={fiscalMinDate}
                      maxDate={latestShareMonthMaxDate}
                      dateFormat="MMMM yyyy"
                    />
                    {errors.share_month && <h4 className="text-error">{errors.share_month}</h4>}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_processed_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select date"
                      className="form-control no-drag"
                      selected={formData.processed_date}
                      onChange={(date) => handleChange("processed_date", date ?? new Date())}
                      minDate={minDate}
                      maxDate={maxDate}
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.processed_date && <h4 className="text-error">{errors.processed_date}</h4>}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_add_fund_amount" />}
                    </Label>
                    <Input
                      name="amount"
                      type="text"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.amount}
                      disabled={loading.calculate}
                    />
                    {loading.calculate ? (
                      <small className="text-muted d-block mt-1">Calculating share...</small>
                    ) : (
                      isDeficit && (
                        <div
                          style={{
                            backgroundColor: "#FEF3C7",
                            border: "1px solid #F59E0B",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            marginTop: "6px",
                            color: "#92400E",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          <i className="fa fa-exclamation-triangle mr-1" style={{ color: "#D97706", marginRight: "4px" }}></i>
                          {t("markaz_share_deficit_warning", { amount: calculatedAmount.toLocaleString() })}
                        </div>
                      )
                    )}
                    {errors.amount && <h4 className="text-error no-drag">{errors.amount}</h4>}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="transactions_memo" />}
                    </Label>
                    <Input
                      name="memo"
                      type="text"
                      value={formData.memo}
                      onChange={(e) => handleChange("memo", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.memo}
                    />
                    {errors.memo && <h4 className="text-error no-drag">{errors.memo}</h4>}
                  </FormGroup>
                </Col>
              </Row>

              {isDeficit && (
                <Row className="mt-2">
                  <Col md="6">
                    <div
                      className="no-drag"
                      style={{
                        backgroundColor: "#FEF3C7",
                        border: "1px solid #F59E0B",
                        borderRadius: "8px",
                        padding: "12px 14px",
                      }}
                    >
                      <FormGroup className="mb-0">
                        <Label className="col-form-label" style={{ color: "#92400E", fontWeight: 700 }}>
                          <i className="fa fa-paperclip" style={{ marginRight: "6px", color: "#D97706" }}></i>
                          {<GetLanguageString props="markaz_share_justification_attachment" />}
                        </Label>
                        <Input
                          type="file"
                          accept="image/*,application/pdf"
                          name="file"
                          className="form-control"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              if (file.size > 2 * 1024 * 1024) {
                                setJustificationFile(null);
                                setErrors((prev) => ({
                                  ...prev,
                                  justificationFile: t("markaz_share_justification_size_limit"),
                                }));
                                e.target.value = "";
                              } else {
                                setJustificationFile(file);
                                setErrors((prev) => ({ ...prev, justificationFile: "" }));
                              }
                            } else {
                              setJustificationFile(null);
                            }
                          }}
                        />
                        {errors.justificationFile && (
                          <h4 className="text-error">{errors.justificationFile}</h4>
                        )}
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
              )}

              <Row className="mt-3">
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  <Button
                    type="submit"
                    color="primary"
                    disabled={loading.submit || loading.calculate}
                  >
                    {<GetLanguageString props="funds_submit" />}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(MarkazShare);
